import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    CheckCircle2,
    Info,
    Pencil,
    Save,
    Loader2,
    Calendar,
    Users,
    Trophy,
    Lock
} from 'lucide-react';
import { calculateSetScore } from '../../config/sportStats';
import GameDetailModal from '../Game/GameDetailModal';
import './TeamCallUpHistoryModal.css';
import API_BASE_URL from '../../config/api';

const TeamCallUpHistoryModal = ({ isOpen, onClose, teamId, athletes, sportName }) => {
    const [games, setGames] = useState([]);
    const [callUps, setCallUps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [modifiedGames, setModifiedGames] = useState(new Set());
    const [matrix, setMatrix] = useState({});
    const [gameResults, setGameResults] = useState({});

    const [drawerGame, setDrawerGame] = useState(null);
    const [drawerTab, setDrawerTab] = useState('result');
    const [drawerFocusAthleteId, setDrawerFocusAthleteId] = useState(null);

    const scrollRef = useRef(null);

    useEffect(() => {
        if (isOpen && teamId) fetchHistory();
    }, [isOpen, teamId]);

    useEffect(() => {
        if (!loading && games.length > 0) {
            scrollToClosestDate();
            fetchAllGameResults();
        }
    }, [loading, games]);

    const fetchHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/game-callups/team-history/${teamId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Falha ao carregar histórico de convocatórias.');
            const data = await response.json();

            setGames(data.games);
            setCallUps(data.callUps);

            const initialMatrix = {};
            data.games.forEach(g => { initialMatrix[g.id] = {}; });
            data.callUps.forEach(c => {
                if (initialMatrix[c.eventId]) initialMatrix[c.eventId][c.athleteId] = true;
            });
            setMatrix(initialMatrix);
            setModifiedGames(new Set());
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllGameResults = async () => {
        try {
            const token = localStorage.getItem('token');
            const results = await Promise.allSettled(
                games.map(g =>
                    fetch(`${API_BASE_URL}/api/game-results/${g.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(r => r.ok ? r.json() : null)
                )
            );
            const map = {};
            results.forEach((r, i) => {
                if (r.status === 'fulfilled' && r.value?.result) {
                    map[games[i].id] = r.value.result;
                }
            });
            setGameResults(map);
        } catch (err) {
            console.warn('Result fetch warning:', err);
        }
    };

    const scrollToClosestDate = () => {
        if (!scrollRef.current) return;
        const now = new Date();
        let closestIndex = 0;
        let minDiff = Infinity;
        games.forEach((game, index) => {
            const diff = Math.abs(new Date(game.startDateTime) - now);
            if (diff < minDiff) { minDiff = diff; closestIndex = index; }
        });
        setTimeout(() => {
            const container = scrollRef.current;
            if (!container) return;
            const headers = container.querySelectorAll('th');
            const targetHeader = headers[closestIndex + 1];
            if (targetHeader) {
                const stickyWidth = headers[0].offsetWidth;
                container.scrollTo({ left: targetHeader.offsetLeft - stickyWidth - 20, behavior: 'smooth' });
            }
        }, 300);
    };

    const toggleCallUp = (eventId, athleteId) => {
        const game = games.find(g => g.id === eventId);
        if (game && new Date(game.startDateTime) < new Date()) return;

        setMatrix(prev => ({
            ...prev,
            [eventId]: { ...prev[eventId], [athleteId]: !prev[eventId][athleteId] }
        }));
        setModifiedGames(prev => new Set(prev).add(eventId));
    };

    const handleSave = async () => {
        if (modifiedGames.size === 0) { onClose(); return; }
        setSaving(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const changes = Array.from(modifiedGames).map(eventId => ({
                eventId,
                athleteIds: Object.keys(matrix[eventId])
                    .filter(aId => matrix[eventId][aId])
                    .map(aId => parseInt(aId))
            }));
            const response = await fetch(`${API_BASE_URL}/api/game-callups/batch-update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ changes })
            });
            if (!response.ok) throw new Error('Falha ao guardar alterações.');
            setModifiedGames(new Set());
            onClose();
            window.dispatchEvent(new CustomEvent('dashboardRefresh'));
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const openDrawer = (game, tab = 'result', athleteId = null) => {
        setDrawerGame(game);
        setDrawerTab(tab);
        setDrawerFocusAthleteId(athleteId);
    };

    const closeDrawer = () => {
        setDrawerGame(null);
        setDrawerFocusAthleteId(null);
        fetchAllGameResults();
    };

    const getResultBadge = (game) => {
        const result = gameResults[game.id];
        if (!result) return null;
        if (!result.isFinished) return <span className="result-badge draft">rascunho</span>;

        const isHome = game.isHomeGame ?? true;
        const opponent = game.opponentName || 'Visitante';
        const homeName = isHome ? 'Nós' : opponent;
        const awayName = isHome ? opponent : 'Nós';

        if (result.setsJson) {
            try {
                const sets = JSON.parse(result.setsJson);
                const { homeSets, awaySets } = calculateSetScore(sets);
                return (
                    <span className="result-badge final">
                        <span className="team-abbr">{homeName}</span> {homeSets}–{awaySets} <span className="team-abbr">{awayName}</span>
                    </span>
                );
            } catch { return null; }
        }
        if (result.homeScore != null && result.awayScore != null) {
            return (
                <span className="result-badge final">
                    <span className="team-abbr">{homeName}</span> {result.homeScore}–{result.awayScore} <span className="team-abbr">{awayName}</span>
                </span>
            );
        }
        return null;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="callup-history-modal" onClick={e => e.stopPropagation()}>
                    <header className="modal-header">
                        <div className="header-title">
                            <div className="icon-box">
                                <Trophy size={22} />
                            </div>
                            <div>
                                <h2>Histórico de Convocatórias</h2>
                                <p className="subtitle">Gestão de atletas e resultados de jogos</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={onClose} aria-label="Fechar">
                            <X size={20} />
                        </button>
                    </header>

                    <div className="modal-body">
                        {loading ? (
                            <div className="loading-state">
                                <Loader2 className="spinner" />
                                <span>A carregar histórico...</span>
                            </div>
                        ) : error ? (
                            <div className="error-state">
                                <Info size={40} />
                                <span>{error}</span>
                            </div>
                        ) : (
                            <div className="history-table-container" ref={scrollRef}>
                                <table className="history-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-col athlete-header">
                                                <div className="header-cell">
                                                    <Users size={16} />
                                                    Atleta
                                                </div>
                                            </th>
                                            {games.map(game => (
                                                <th
                                                    key={game.id}
                                                    className="game-col-header"
                                                    title="Clique para ver resultado e estatísticas"
                                                    onClick={() => openDrawer(game, 'result')}
                                                >
                                                    <div className="game-header-content">
                                                        <div className="date-badge">
                                                            <Calendar size={12} />
                                                            {new Date(game.startDateTime).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                                                        </div>
                                                        <span className="game-title" title={game.title}>{game.title}</span>
                                                        {getResultBadge(game)}
                                                        <div className="edit-hint">
                                                            <Pencil size={12} />
                                                        </div>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {athletes.map(athlete => (
                                            <tr key={athlete.id} className="athlete-row">
                                                <td className="sticky-col athlete-name-cell">
                                                    <div className="athlete-info">
                                                        <div className="avatar-mini hi-mobile">
                                                            {athlete.name?.charAt(0)}
                                                        </div>
                                                        <span>{athlete.name}</span>
                                                    </div>
                                                </td>
                                                {games.map(game => {
                                                    const isCalled = matrix[game.id]?.[athlete.id];
                                                    const isPastGame = new Date(game.startDateTime) < new Date();

                                                    return (
                                                        <td
                                                            key={`${game.id}-${athlete.id}`}
                                                            className={`status-cell ${isCalled ? 'is-called' : ''} ${isPastGame ? 'is-past' : ''}`}
                                                            onClick={() => !isPastGame && toggleCallUp(game.id, athlete.id)}
                                                            onContextMenu={e => {
                                                                e.preventDefault();
                                                                openDrawer(game, 'stats', athlete.id);
                                                            }}
                                                            title={isPastGame ? "Jogo já realizado - Convocatória bloqueada" : "Clique para convocar / Botão direito para estatísticas"}
                                                        >
                                                            <div className="status-indicator">
                                                                {isPastGame ? (
                                                                    isCalled ? <CheckCircle2 size={18} className="check-icon" /> : <Lock size={14} className="lock-icon" />
                                                                ) : (
                                                                    isCalled ? (
                                                                        <CheckCircle2 size={20} className="check-icon" />
                                                                    ) : (
                                                                        <div className="empty-dot" />
                                                                    )
                                                                )}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <footer className="modal-footer">
                        <div className="footer-legend">
                            <div className="legend-item">
                                <div className="legend-dot active" />
                                <span>Convocado</span>
                            </div>
                            <div className="legend-info">
                                <Info size={14} />
                                <span>Botão direito na célula para estatísticas individuais</span>
                            </div>
                        </div>
                        <div className="footer-actions">
                            <button className="btn-secondary" onClick={onClose} disabled={saving}>
                                Cancelar
                            </button>
                            <button
                                className={`btn-primary ${modifiedGames.size > 0 ? 'pulse' : ''}`}
                                onClick={handleSave}
                                disabled={saving || (!loading && modifiedGames.size === 0)}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="spinner-small" />
                                        <span>A guardar...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        <span>Guardar Alterações {modifiedGames.size > 0 && `(${modifiedGames.size})`}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </footer>
                </div>
            </div>

            <GameDetailModal
                isOpen={!!drawerGame}
                onClose={closeDrawer}
                game={drawerGame}
                sportName={sportName}
                initialTab={drawerTab}
                focusAthleteId={drawerFocusAthleteId}
            />
        </>
    );
};

export default TeamCallUpHistoryModal;
